import { useMemo, useState } from 'react';
import type { IphoneSmsConversation } from '@casetrace/shared';
import { Play } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ConversationFilters } from './conversation-filters';
import { ConversationRow } from './conversation-row';
import { useConversationSelection } from './use-conversation-selection';
import type { IphoneSmsImportSelection } from './use-iphone-sms-import';

// Meldingstidsstempler er UTC (fra parseAppleMessageDate), så datogrensene
// tolkes også i UTC – ellers forskyves grensene med nettleserens tidssone.
function toStartOfDayIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function toEndOfDayIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  return new Date(`${value}T23:59:59.999Z`).toISOString();
}

function matchesSearch(conversation: IphoneSmsConversation, term: string): boolean {
  if (!term) {
    return true;
  }
  const haystack = `${conversation.displayName} ${conversation.chatIdentifier ?? ''}`.toLowerCase();
  return haystack.includes(term.toLowerCase());
}

type ConversationListProps = {
  readonly conversations: IphoneSmsConversation[];
  readonly totalMessages?: number;
  readonly onImport: (selection: IphoneSmsImportSelection) => void;
  readonly isImporting: boolean;
  readonly importError?: string;
  readonly hasStarted: boolean;
};

export function ConversationList({
  conversations,
  totalMessages,
  onImport,
  isImporting,
  importError,
  hasStarted,
}: ConversationListProps) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const visible = useMemo(
    () => conversations.filter((conversation) => matchesSearch(conversation, search)),
    [conversations, search],
  );
  const { selected, allVisibleSelected, toggle, toggleAllVisible } =
    useConversationSelection(visible);

  const handleImport = () => {
    onImport({
      conversationIds: [...selected],
      dateFrom: toStartOfDayIso(dateFrom),
      dateTo: toEndOfDayIso(dateTo),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8b8d9a]">
          {conversations.length} samtaler
          {typeof totalMessages === 'number' ? ` · ${totalMessages} meldinger` : ''}
        </p>
        <button
          type="button"
          className="text-xs text-accent hover:underline"
          onClick={toggleAllVisible}
          disabled={visible.length === 0}
        >
          {allVisibleSelected ? 'Fjern alle' : 'Velg alle'}
        </button>
      </div>

      <ConversationFilters
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={setSearch}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      />

      <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="text-xs text-[#5e6070]">Ingen samtaler matcher søket.</p>
        ) : (
          visible.map((conversation) => (
            <ConversationRow
              key={conversation.chatId}
              conversation={conversation}
              checked={selected.has(conversation.chatId)}
              onToggle={toggle}
            />
          ))
        )}
      </div>

      {hasStarted && (
        <p className="text-xs text-success">
          Importen er startet. Følg fremdriften under importjobber.
        </p>
      )}
      {importError && <p className="text-xs text-danger/80">{importError}</p>}

      <Button
        variant="primary"
        type="button"
        disabled={selected.size === 0 || isImporting}
        onClick={handleImport}
      >
        <Play size={14} />
        {isImporting ? 'Starter import...' : `Importer valgte (${selected.size})`}
      </Button>
    </div>
  );
}
